import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Route, PlusCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { photoUrl } from "@/lib/api";
import { institutionLabelT } from "@/lib/labels";
import { handoffsApi } from "@/lib/endpoints";

// Public chain-of-custody timeline for a case. Shows photo, institution, optional
// receiver name and timestamp. Never shows cédula or exact GPS (not in the payload).
export function HandoffTimeline({ caseId }) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const locale = i18n.language?.startsWith("en") ? "en-US" : "es-VE";

  useEffect(() => {
    handoffsApi.list(caseId).then(setItems).catch(() => {});
  }, [caseId]);

  return (
    <div className="bg-white border border-neutral-200 rounded-sm p-4 flex flex-col gap-3" data-testid="handoff-timeline">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-neutral-900 flex items-center gap-1.5">
          <Route className="w-4 h-4 text-[#0066CC]" /> {t("timeline.title")}
        </p>
        <Link to={`/caso/${caseId}/entrega`} data-testid="register-handoff-btn"
          className="text-sm text-[#0066CC] hover:underline flex items-center gap-1 shrink-0">
          <PlusCircle className="w-4 h-4" /> {t("timeline.register")}
        </Link>
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-neutral-500" data-testid="no-handoffs">
          {t("timeline.empty")}
        </p>
      ) : (
        <ol className="flex flex-col gap-3">
          {items.map((h, i) => (
            <li key={h.id} className="flex gap-3" data-testid={`handoff-${h.id}`}>
              <div className="flex flex-col items-center">
                <span className="w-6 h-6 rounded-full bg-[#0066CC] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                {i < items.length - 1 && <span className="flex-1 w-0.5 bg-neutral-200 my-1" />}
              </div>
              <div className="flex gap-3 flex-1 pb-1">
                <img src={photoUrl(h.photo_path)} alt="" className="w-16 h-16 object-cover rounded-sm bg-neutral-200 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-neutral-900">{institutionLabelT(h.institution, t)}</p>
                  {h.receiver_name && <p className="text-sm text-neutral-700">{t("timeline.receivedBy", { name: h.receiver_name })}</p>}
                  <p className="text-xs text-neutral-500">{new Date(h.created_at).toLocaleString(locale)}</p>
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
